/**
 * BulkEarningTable — LiveView hook for inline-editable earning item tables.
 *
 * Built on createBulkTableHook for shared behavior (dirty tracking, defaults,
 * save, errors, filtering). Adds earning-specific:
 * - Client-side computed fields (quantity, amount) based on calculation_type
 * - Date input clamping
 * - Row cache with qtyDisplay / amountDisplay elements
 */

import createBulkTableHook from "./createBulkTableHook";

const DAYS_IN_MONTH = 30;

function clampDateInput(input: HTMLInputElement): void {
  if (input.type !== "date" || !input.value) return;
  if (input.min && input.value < input.min) input.value = input.min;
  if (input.max && input.value > input.max) input.value = input.max;
}

function dateDiffDays(startStr: string, endStr: string): number {
  const start = new Date(startStr + "T00:00:00");
  const end = new Date(endStr + "T00:00:00");
  return Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
}

function calculateQuantity(
  startDate: string,
  endDate: string,
  unit: string,
  payrunStartDate: string,
): number {
  if (!startDate || !endDate) return 0;

  const rawDays = dateDiffDays(startDate, endDate);
  if (rawDays <= 0) return 0;

  if (unit === "months") {
    const payrunStart = new Date(payrunStartDate + "T00:00:00");
    const daysInMonth = new Date(
      payrunStart.getFullYear(),
      payrunStart.getMonth() + 1,
      0,
    ).getDate();
    return rawDays >= daysInMonth ? 30 : rawDays;
  }

  return rawDays;
}

function calculateAmount(
  calculationType: string,
  baseAmount: number,
  rate: number,
  quantity: number,
  currentValue: number,
): number {
  switch (calculationType) {
    case "prorated_rate":
      return baseAmount * (quantity / DAYS_IN_MONTH);
    case "rate_based":
      return rate * quantity;
    case "flat_rate":
      return currentValue;
    default:
      return 0;
  }
}

function roundMoney(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

function roundQuantity(value: number): string {
  return (Math.round(value * 10000) / 10000).toFixed(4);
}

// Recalculate computed fields for a single row using cached elements
function recalculateRow(this: any, rowId: string): void {
  const cache = this.rowCache.get(rowId);
  if (!cache) return;

  const val = (field: string) => cache.inputs.get(field)?.value || "";

  const startDate = val("start_date");
  const endDate = val("end_date");
  const baseAmount = parseFloat(val("base_amount")) || 0;
  const rate = parseFloat(val("rate")) || 0;
  const currentValueInput = parseFloat(val("current_value")) || 0;

  let quantity = 0;
  if (
    this.calculationType === "prorated_rate" ||
    (this.calculationType === "rate_based" && this.unit !== "hours")
  ) {
    quantity = calculateQuantity(
      startDate,
      endDate,
      this.unit,
      this.payrunStartDate,
    );
    if (cache.qtyDisplay) {
      cache.qtyDisplay.textContent =
        quantity > 0 ? roundQuantity(quantity) : "--";
    }
  } else if (this.calculationType === "rate_based" && this.unit === "hours") {
    quantity = parseFloat(val("quantity")) || 0;
  }

  const amount = calculateAmount(
    this.calculationType,
    baseAmount,
    rate,
    quantity,
    currentValueInput,
  );

  if (cache.amountDisplay) {
    cache.amountDisplay.textContent = amount >= 0 ? roundMoney(amount) : "--";
  }
}

const base = createBulkTableHook({
  buildRowCacheExtras: (tr: HTMLElement) => ({
    qtyDisplay: tr?.querySelector("[data-computed-qty]") || null,
    amountDisplay: tr?.querySelector("[data-computed-amount]") || null,
  }),
  onRowChange(this: any, rowId: string) {
    recalculateRow.call(this, rowId);
  },
});

// Override mounted to read earning-specific dataset attributes, add date clamping
const baseMounted = base.mounted;
const baseAttachEventListeners = base.attachEventListeners;

const BulkEarningTable = {
  ...base,

  mounted(this: any) {
    this.calculationType = this.el.dataset.calculationType;
    this.unit = this.el.dataset.unit;
    this.payrunStartDate = this.el.dataset.payrunStartDate;

    baseMounted.call(this);
  },

  attachEventListeners(this: any) {
    baseAttachEventListeners.call(this);

    // Add date clamping on change (after base change listener)
    this.el.addEventListener("change", (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (!target.dataset?.rowId) return;
      clampDateInput(target);
    });
  },

  // Expose for direct calls (e.g. from tests)
  recalculateRow,
};

export default BulkEarningTable;
